import React, { useState } from 'react';
import styled from 'styled-components';
import { Icon, IconName } from '../icon';
import { Tooltip, TooltipPositions } from '../tooltip';
import { Box } from '../primitives';
import rem from '../../utils/rem';
import { theme as UITheme } from '../../theme';

export const PC_BUTTON_WIDTH = 352;

interface IStyleButton {
  className?: string;
  variant?: 'outlined' | 'contained';
  color?: string; // primary, subscription, secondary , color hexa or inerhit
  icon?: IconName;
  text?: string;
  size?: 'small' | 'medium' | 'large'; // usual = small for classic button no icon, medium for classic button with icon, large used for big buttons with icons (send-message / photos gallery buttons)
  shadow?: boolean;
  disabled?: boolean;
  noHover?: boolean;
}

export interface IButton extends IStyleButton {
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  tooltip?: string;
  tooltipPosition?: TooltipPositions;
  'data-test'?: string;
  onMouseEnter?: () => void;
}

// desctructured
export const Button = ({
  children, // only used in UploadPhoto to pass an input as children
  className,
  size = 'medium',
  tooltipPosition = 'left',
  variant,
  color,
  icon,
  text,
  shadow,
  onClick,
  disabled,
  tooltip,
  noHover,
  'data-test': dataTest,
  onMouseEnter,
}: IButton) => {
  const [tooltipShowed, setTooltipShowed] = useState(false);

  return (
    <SButton
      className={className}
      onClick={onClick}
      onMouseOver={() => {
        if (tooltip != null) {
          setTooltipShowed(true);
        }
      }}
      onMouseOut={() => {
        setTooltipShowed(false);
      }}
      onMouseEnter={onMouseEnter}
      size={size}
      variant={variant}
      color={color}
      icon={icon}
      text={text}
      shadow={shadow}
      disabled={disabled}
      noHover={noHover}
      data-test={dataTest}
    >
      {icon && text && (
        <Icon
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          style={{ marginRight: '10px' }}
          size={UITheme.iconSizes[size]}
          name={icon}
        ></Icon>
      )}
      {icon && !text && (
        <Icon size={UITheme.iconSizes[size]} name={icon}></Icon>
      )}
      {text && <span>{text}</span>}
      {tooltip && tooltipShowed && (
        <Box
          position="absolute"
          zIndex={1}
          left={tooltipPosition === 'right' ? '100%' : 'initial'}
          ml={tooltipPosition === 'right' ? rem(10) : '0px'}
          right={
            tooltipPosition === 'left'
              ? '100%'
              : tooltipPosition === 'top'
              ? '50%'
              : 'initial'
          }
          mr={tooltipPosition === 'left' ? rem(10) : '0px'}
          top={tooltipPosition === 'top' ? undefined : '50%'}
          mt={tooltipPosition === 'top' ? undefined : rem(-12)}
          bottom={tooltipPosition === 'top' ? '100%' : undefined}
          mb={tooltipPosition === 'top' ? rem(10) : undefined}
          transform={tooltipPosition === 'top' ? 'translateX(50%)' : undefined}
        >
          <Tooltip label={tooltip} position={tooltipPosition} />
        </Box>
      )}
      {children}
    </SButton>
  );
};

const SButton = styled('button').withConfig({
  shouldForwardProp: props =>
    ![
      'color',
      'icon',
      'noHover',
      'shadow',
      'size',
      'text',
      'tooltip',
      'tooltipPosition',
      'variant',
    ].includes(props),
})<IStyleButton>`
  white-space: nowrap;
  position: relative;
  outline: none;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  transition: background-color 200ms ease 0s, color 200ms ease 0s;
  line-height: ${rem(22)};
  font-weight: 600;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ text }) => (text != null ? rem(50) : '50%')};
  border-width: ${({ variant }) => (variant === 'outlined' ? '2px' : 0)};
  border-style: ${({ variant }) => (variant === 'outlined' ? 'solid' : null)};
  border-color: ${({ theme, variant, disabled, color }) => {
    let local_color: string | undefined;

    if (variant === 'outlined') {
      if (disabled) {
        return theme.colors.grey1;
      }

      switch (color) {
        case 'primary':
          local_color = theme.colors.primary2;
          break;
        case 'subscription':
          local_color = theme.colors.subscription;
          break;
        case 'secondary':
          local_color = theme.colors.secondary;
          break;
        default:
          local_color = color !== undefined ? color : 'inherit';
          break;
      }
    }

    return local_color;
  }}}
  color: ${({ color, theme, disabled, variant }) => {
    let local_color: string | undefined;

    if (disabled) {
      if (variant === 'outlined') {
        return theme.colors.grey1;
      } else {
        return theme.colors.light;
      }
    }

    if (variant === 'contained') {
      return theme.colors.light;
    } // (props.variant === 'outlined' | null) {
    else
      switch (color) {
        case 'grey':
          local_color = theme.colors.grey1;
          break;
        case 'light':
          local_color = theme.colors.light;
          break;
        case 'primary':
          local_color = theme.colors.primary2;
          break;
        case 'subscription':
          local_color = theme.colors.subscription;
          break;
        case 'secondary':
          local_color = theme.colors.secondary;
          break;
        case 'secondary2':
          local_color = theme.colors.secondary2;
          break;
        default:
          local_color = color !== undefined ? color : 'inherit';
          break;
      }

    return local_color;
  }};
  background-color: ${({ disabled, theme, variant, color }) => {
    let bgColor: string | undefined;

    if (disabled) {
      if (variant === 'contained') {
        return theme.colors.disabled;
      } else {
        return theme.colors.light;
      }
    }

    if (variant === 'contained') {
      switch (color) {
        case 'light':
          bgColor = theme.colors.darkTranslucent;
          break;
        case 'primary':
          bgColor = theme.colors.primary2
            ? theme.colors.primary2
            : theme.colors.raspberry;
          break;
        case 'subscription':
          bgColor = theme.colors.subscription;
          break;
        case 'secondary':
          bgColor = theme.colors.secondary2;
          break;
        default:
          bgColor =
            color !== undefined ? color : theme.colors.transparent_white;
          break;
      }
    } else {
      bgColor = theme.colors.light;
    }

    return bgColor;
  }};
  font-size: ${({ size, theme }) => {
    switch (size) {
      case 'small':
        return theme.fontSizes.size16;
      case 'large':
        return theme.fontSizes.size24;
      case 'medium':
      default:
        return theme.fontSizes.size16;
    }
  }};
  height: ${({ size }) => {
    switch (size) {
      case 'small':
        return rem(32);
      case 'large':
        return rem(72);
      case 'medium':
      default:
        return rem(50);
    }
  }};
  min-width: ${({ size }) => {
    switch (size) {
      case 'small':
        return rem(32);
      case 'large':
        return rem(72);
      case 'medium':
      default:
        return rem(50);
    }
  }};
  padding: ${({ text, icon, size }) => {
    const pt = `${rem(7)} `,
      pb = `${rem(9)} `;
    let pr = '0 ',
      pl = '0';

    if (text != null) {
      if (icon != null) {
        pr = '1rem ';
        pl = '1rem ';

        if (size === 'medium') {
          pr = '1.25rem ';
          pl = '1.25rem ';
        }
      } else {
        pr = '1.5rem ';
        pl = '1.5rem ';
      }
    } else {
      return 0;
    }

    return pt + pr + pb + pl;
  }};
  box-shadow: ${({ shadow }) =>
    shadow ? 'rgb(0 0 0 / 10%) 0px 2px 15px 0px' : null};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: ${({ disabled, noHover, theme, color, variant }) => {
        if (disabled || noHover) {
          return;
        }

        if (variant === 'contained') {
          switch (color) {
            case 'light':
              return 'rgba(0,0,0,1)';
            case 'primary':
              return theme.colors.primaryHoverContained;
            case 'subscription':
              return theme.colors.subscriptionHoverContained;
            case 'secondary':
              return theme.colors.secondaryHoverContained;
            default:
              return '';
          }
        } else {
          switch (color) {
            case 'primary':
              return theme.colors.primaryHover;
            case 'subscription':
              return theme.colors.subscriptionHover;
            case 'secondary':
            case 'secondary2':
              return theme.colors.secondaryHover;
            case 'light':
              return 'rgba(0,0,0,1)';
            default:
              return 'rgba(0,0,0,.05)';
          }
        }
      }};
    }
  }
`;

export const SPCButton = styled(Button)<{
  hasHover?: boolean;
  disabled?: boolean;
}>`
  height: auto;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.size18};
  border-radius: ${({ theme }) => theme.radii.small};
  line-height: ${rem(24)};
  padding: ${({ theme }) =>
    `${rem(8)} ${theme.space.medium} ${rem(8)} ${theme.space.medium}`};

  ${({ theme }) => theme.mediaQueries.md} {
    min-width: ${PC_BUTTON_WIDTH}px;
  }

  ${({ hasHover, disabled, theme }) =>
    hasHover
      ? `
      &:active {
        background-color: ${
          theme.colors[disabled ? 'pc_darkTranslucent' : 'lightHover']
        };
      }
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background-color: 
            ${theme.colors[disabled ? 'pc_darkTranslucent' : 'lightHover']};
        }
      }`
      : ``}
`;
